/**
 * Recommendations slider inside the cart drawer.
 * Custom element, so it re-inits whenever the Section Rendering API replaces the markup.
 */
if (!customElements.get("related-products-slider")) {
  customElements.define(
    "related-products-slider",
    class RelatedProductsSlider extends HTMLElement {
      static AUTOPLAY_MEDIA_QUERY = "(max-width: 992px)";
      static AUTOPLAY_DELAY = 3000;

      /** Inits Swiper on the current markup. */
      connectedCallback() {
        this.container = this.querySelector(".swiper");
        if (!this.container || typeof Swiper === "undefined") return;

        // Latched at init: the instance is configured once, so a later viewport change
        // must not make start()/stop() disagree with the autoplay the instance actually has.
        this.autoplayEnabled = window.matchMedia(
          RelatedProductsSlider.AUTOPLAY_MEDIA_QUERY,
        ).matches;

        const options = {
          slidesPerView: "auto",
          speed: 1000,
          spaceBetween: 10,
          grabCursor: true,
          watchSlidesProgress: true,
          // Without arrows this is the only way to reach the off-screen slides without a mouse.
          // onlyInViewport keeps the closed drawer from swallowing arrow keys.
          keyboard: { enabled: true, onlyInViewport: true },
          autoplay: this.autoplayEnabled
            ? {
                delay: RelatedProductsSlider.AUTOPLAY_DELAY,
                disableOnInteraction: false,
              }
            : false,
        };

        // Controls are optional, so only wire up the modules that have markup.
        const nextEl = this.querySelector(".swiper-button-next");
        const prevEl = this.querySelector(".swiper-button-prev");
        const paginationEl = this.querySelector(".swiper-pagination");

        if (nextEl && prevEl) options.navigation = { nextEl, prevEl };
        if (paginationEl)
          options.pagination = { el: paginationEl, clickable: true };

        this.swiper = new Swiper(this.container, options);

        // the classes these keep in sync are what decides which slides are reachable
        this.swiper.on("transitionEnd", () => this.syncReachability());
        this.swiper.on("resize", () => this.syncReachability());
        this.swiper.on("update", () => this.syncReachability());

        this.watchDrawer();
        this.watchFocus();
        this.syncReachability();
      }

      /** Tears down the instance, its observer and the focus handlers. */
      disconnectedCallback() {
        this.drawerObserver?.disconnect();
        this.drawerObserver = null;

        if (this.onFocusIn) this.removeEventListener("focusin", this.onFocusIn);
        if (this.onFocusOut)
          this.removeEventListener("focusout", this.onFocusOut);
        this.onFocusIn = null;
        this.onFocusOut = null;

        if (this.swiper && !this.swiper.destroyed) {
          this.swiper.destroy(true, true);
        }
        this.swiper = null;
      }

      /**
       * Slides with no part in view leave the tab order: reaching one with Tab puts focus
       * on a card nobody can see. The arrow keys move the carousel, and each slide becomes
       * reachable as it arrives. A slide showing at the edge stays reachable on purpose --
       * inert takes the pointer with it, and a card the user can see and click should not
       * become unclickable. onFocusIn brings that one fully into view for keyboard focus.
       */
      syncReachability() {
        if (!this.swiper || this.swiper.destroyed) return;

        this.swiper.slides.forEach((slide) => {
          // inert on an ancestor blurs whatever it holds, so the slide the caret is in
          // keeps its place until the carousel has actually moved it away
          slide.inert =
            !slide.classList.contains("swiper-slide-visible") &&
            !slide.contains(document.activeElement);
        });
      }

      /** Brings a keyboard-focused slide into view, and holds autoplay while focus is inside. */
      watchFocus() {
        this.onFocusIn = (event) => {
          if (this.autoplayEnabled) this.swiper.autoplay.stop();

          // DEV NOTE: Keyboard focus only: a click on the card cut off at the edge focuses it too, and
          // must not scroll the carousel. :focus-visible is false for mouse, touch, and the
          // focus a quick-add modal returns to its opener.
          if (!event.target.matches(":focus-visible")) return;

          const slide = event.target.closest(".swiper-slide");
          if (!slide || slide.classList.contains("swiper-slide-fully-visible"))
            return;

          const index = this.swiper.slides.indexOf(slide);
          if (index > -1) this.swiper.slideTo(index);
        };

        this.onFocusOut = (event) => {
          // A quick-add modal lives in <body>, so opening one looks like leaving the carousel. Autoplay
          // stays paused behind it: if the carousel advanced, syncReachability() would make the opener's
          // slide inert and closing the modal could no longer return focus to the button.
          if (
            this.contains(event.relatedTarget) ||
            event.relatedTarget?.closest?.("quick-add-modal")
          )
            return;
          if (
            this.autoplayEnabled &&
            this.drawer?.classList.contains("active")
          ) {
            this.swiper.autoplay.start();
          }
        };

        this.addEventListener("focusin", this.onFocusIn);
        this.addEventListener("focusout", this.onFocusOut);
      }

      /** Tracks the open state of the drawer. */
      watchDrawer() {
        this.drawer = this.closest("cart-drawer");
        if (!this.drawer) return;

        this.onDrawerToggle = this.onDrawerToggle.bind(this);
        this.drawerObserver = new MutationObserver(this.onDrawerToggle);
        this.drawerObserver.observe(this.drawer, {
          attributes: true,
          attributeFilter: ["class"],
        });

        this.onDrawerToggle();
      }

      /** Re-measures and runs autoplay only while the drawer is on screen. */
      onDrawerToggle() {
        if (!this.swiper || this.swiper.destroyed) return;

        if (!this.drawer.classList.contains("active")) {
          if (this.autoplayEnabled) this.swiper.autoplay.stop();
          return;
        }

        this.swiper.update();
        this.syncReachability();
        if (this.autoplayEnabled) this.swiper.autoplay.start();
      }
    },
  );
}

/**
 * DEV NOTE: Cart drawer recommendations.
 *
 * Loads when the drawer opens: <product-recommendations> (global.js) waits for its element to
 * scroll into view, which never happens here (the placeholder has no height and sits at the end
 * of the drawer's scrolling list).
 *
 * The drawer is repainted via innerHTML after every cart change, which would empty and refetch
 * the block. So each list is cached for a few minutes and repainted instantly. Products that are
 * in the cart by then are dropped on every paint (the cached markup still holds them, so a removed
 * product comes back without a refetch), and a new list is fetched in the background while the
 * previous one stays on screen.
 *
 * Outside the drawer (cart page, search no-results) the element is not repainted on a cart change,
 * so it subscribes to cart updates and seeds itself again from /cart.js.
 */
if (!customElements.get("cart-recommendations")) {
  customElements.define(
    "cart-recommendations",
    class CartRecommendations extends HTMLElement {
      // Cached server markup per seed list: { markup, time }.
      static cache = new Map();
      // Requests in flight per seed list, so a repaint does not start a second one.
      static requests = new Map();
      // Markup painted last per section, shown while a new seed list loads.
      static lastMarkup = new Map();
      // Lists are fetched again after this long, so prices and stock stay current.
      static CACHE_MS = 5 * 60 * 1000;

      // a hung request must not keep the old list on screen
      static FETCH_TIMEOUT_MS = 5000;

      // Cart products used as seeds; keep in step with related-products-snippet.liquid.
      static MAX_SEEDS = 3;

      cartUpdateUnsubscriber = null;
      // counts the cart syncs, so only the latest one repaints
      syncToken = 0;

      connectedCallback() {
        this.init();

        // Everywhere but in the drawer the element stays in place when the cart changes, so it
        // follows the cart itself.
        if (
          !this.closest("cart-drawer") &&
          typeof subscribe === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            () => this.syncWithCart(),
          );
        }
      }

      // Paints the list for the current seeds: instantly from the cache, otherwise after a fetch.
      init() {
        const { cache, CACHE_MS } = CartRecommendations;

        // No seeds (empty cart, or only free gifts and insurance in it): show the fallback collection. Its own
        // cache key, and no list kept from an earlier cart on screen while it loads.
        this.isFallback = !this.dataset.seedIds;
        // The drawer and the pages request different sections (see related-products-snippet.liquid).
        const key = `${this.dataset.sectionId}:${this.isFallback ? "fallback" : this.dataset.seedIds}`;
        // a response for an earlier cart must not paint over this one
        this.key = key;
        if (this.isFallback)
          CartRecommendations.lastMarkup.delete(this.dataset.sectionId);

        const cached = cache.get(key);

        if (cached && Date.now() - cached.time < CACHE_MS) {
          this.render(cached.markup);
          return;
        }

        this.render(
          CartRecommendations.lastMarkup.get(this.dataset.sectionId) ?? "",
        );

        const drawer = this.closest("cart-drawer");
        if (!drawer || drawer.classList.contains("active")) {
          this.load(key);
          return;
        }

        this.loadWhenDrawerOpens(key, drawer);
      }

      // Seeds again from the cart as it is now, and repaints when that changes anything.
      async syncWithCart() {
        const token = ++this.syncToken;

        let items;
        try {
          const response = await fetch(
            `${window.routes?.cart_url ?? "/cart"}.js`,
            { headers: { Accept: "application/json" } },
          );
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          ({ items } = await response.json());
        } catch (error) {
          console.error(error);
          return;
        }

        // the element is gone, or a newer change started another sync meanwhile
        if (!this.isConnected || token !== this.syncToken) return;

        // same rules as in related-products-snippet.liquid: newest product first, free gifts and the
        // insurance product skipped
        const excludedIds = (this.dataset.excludedIds ?? "").split(",");
        const productIds = [
          ...new Set(items.map((item) => String(item.product_id))),
        ];
        const seedIds = productIds
          .filter((id) => !excludedIds.includes(id))
          .slice(0, CartRecommendations.MAX_SEEDS)
          .join(",");
        const cartProductIds = productIds.join(",");

        if (
          seedIds === this.dataset.seedIds &&
          cartProductIds === this.dataset.cartProductIds
        )
          return;

        this.dataset.seedIds = seedIds;
        this.dataset.cartProductIds = cartProductIds;
        this.init();
      }

      // Loads on the next time the drawer is opened. Starts from "closed" unless the drawer is
      // open right now, so a retry after a failed load waits for a close and a reopen.
      loadWhenDrawerOpens(key, drawer) {
        this.drawerObserver?.disconnect();

        let closed = !drawer.classList.contains("active");
        this.drawerObserver = new MutationObserver(() => {
          if (!drawer.classList.contains("active")) {
            closed = true;
            return;
          }
          if (!closed) return;

          this.drawerObserver.disconnect();
          this.load(key);
        });
        this.drawerObserver.observe(drawer, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber = null;
        this.drawerObserver?.disconnect();
        this.removeModals();
      }

      // DEV NOTE: quick-add modals move to <body> on connect (ModalDialog), so they outlive this
      // element and would pile up with duplicate ids. Each element removes only its own: on a
      // repaint the new one renders before the old disconnects, so removing all would take the
      // new modals too.
      removeModals() {
        this.modals?.forEach((modal) => modal.remove());
        this.modals = [];
      }

      // Fetches the list for a seed list (newest cart products first), or the fallback collection
      // when there are no seeds, and paints it.
      load(key) {
        const { requests, cache } = CartRecommendations;

        if (!requests.has(key)) {
          const request = this.isFallback
            ? this.fetchFallback()
            : this.fetchFirstList(this.dataset.seedIds.split(","));

          requests.set(
            key,
            request
              .then((markup) => {
                cache.set(key, { markup, time: Date.now() });
                return markup;
              })
              .finally(() => requests.delete(key)),
          );
        }

        requests
          .get(key)
          .then((markup) => {
            // the drawer may have been repainted meanwhile (the new element reads the cache), or
            // this element seeded itself again for a newer cart
            if (this.isConnected && this.key === key) this.render(markup);
          })
          .catch((error) => {
            console.error(error);
            if (!this.isConnected || this.key !== key) return;

            // better no list than an outdated one
            this.render("");

            // nothing is cached on failure: try again the next time the drawer opens
            const drawer = this.closest("cart-drawer");
            if (drawer) this.loadWhenDrawerOpens(key, drawer);
          });
      }

      // A seed without recommendations (gift card, sample, new SKU) falls back to the next one.
      async fetchFirstList(seedIds) {
        for (const productId of seedIds) {
          const markup = await this.fetchList(productId);
          if (markup) return markup;
        }
        return "";
      }

      fetchList(productId) {
        return this.fetchMarkup(
          `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`,
        );
      }

      // The fallback section is rendered in the context of a collection, no seed product needed.
      fetchFallback() {
        const url = this.dataset.fallbackUrl;
        return url ? this.fetchMarkup(url) : Promise.resolve("");
      }

      async fetchMarkup(url) {
        const controller = new AbortController();
        const timer = setTimeout(
          () => controller.abort(),
          CartRecommendations.FETCH_TIMEOUT_MS,
        );

        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const html = document.createElement("div");
          html.innerHTML = await response.text();
          return (
            html.querySelector("product-recommendations")?.innerHTML.trim() ??
            ""
          );
        } finally {
          clearTimeout(timer);
        }
      }

      render(markup) {
        // the fallback list is not a stand-in for the next cart's recommendations
        if (!this.isFallback) {
          CartRecommendations.lastMarkup.set(this.dataset.sectionId, markup);
        }
        this.removeModals();

        const selector = "body > quick-add-modal.quick-add-modal--cart-drawer";
        const before = new Set(document.querySelectorAll(selector));
        this.innerHTML = this.withoutCartItems(markup);
        this.modals = [...document.querySelectorAll(selector)].filter(
          (modal) => !before.has(modal),
        );
      }

      // Drops the slides of products that are in the cart now; "" when none are left.
      withoutCartItems(markup) {
        const inCart = (this.dataset.cartProductIds ?? "").split(",");
        const template = document.createElement("template");
        template.innerHTML = markup;

        template.content
          .querySelectorAll(".swiper-slide[data-product-id]")
          .forEach((slide) => {
            if (inCart.includes(slide.dataset.productId)) slide.remove();
          });

        return template.content.querySelector(".swiper-slide")
          ? template.innerHTML
          : "";
      }
    },
  );
}
